import { createBrowserRouter, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { AppLayout } from './layouts/AppLayout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ModuleGuard } from './components/ModuleGuard';
import { SuperAdminGuard } from './components/SuperAdminGuard';

const PageLoader = () => (
  <div className="flex items-center justify-center h-48">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
  </div>
);

const LoginPage = lazy(() => import('./pages/LoginPage').then(m => ({ default: m.LoginPage })));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const ErrorPage = lazy(() => import('./pages/ErrorPage').then(m => ({ default: m.ErrorPage })));
const DashboardPage = lazy(() => import('./pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const SuperAdminDashboard = lazy(() => import('./pages/admin/SuperAdminDashboard').then(m => ({ default: m.SuperAdminDashboard })));
const AcceptInvitePage = lazy(() => import('./pages/invite/AcceptInvitePage').then(m => ({ default: m.AcceptInvitePage })));
const SitesListPage = lazy(() => import('./pages/sites/SitesListPage').then(m => ({ default: m.SitesListPage })));
const SiteEditPage = lazy(() => import('./pages/sites/SiteEditPage').then(m => ({ default: m.SiteEditPage })));
const SitePreviewPage = lazy(() => import('./pages/sites/SitePreviewPage').then(m => ({ default: m.SitePreviewPage })));
const PagesListPage = lazy(() => import('./pages/pages/PagesListPage').then(m => ({ default: m.PagesListPage })));
const PageEditorPage = lazy(() => import('./pages/pages/PageEditorPage').then(m => ({ default: m.PageEditorPage })));
const MediaLibraryPage = lazy(() => import('./pages/media/MediaLibraryPage').then(m => ({ default: m.MediaLibraryPage })));
const MembersPage = lazy(() => import('./pages/members/MembersPage').then(m => ({ default: m.MembersPage })));
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage').then(m => ({ default: m.SettingsPage })));
const TranslationsPage = lazy(() => import('./pages/sites/TranslationsPage').then(m => ({ default: m.TranslationsPage })));
const ProductsListPage = lazy(() => import('../../../modules/products/src/admin/ProductsListPage').then(m => ({ default: m.ProductsListPage })));
const ProductEditPage = lazy(() => import('../../../modules/products/src/admin/ProductEditPage').then(m => ({ default: m.ProductEditPage })));
const CategoriesPage = lazy(() => import('../../../modules/products/src/admin/CategoriesPage').then(m => ({ default: m.CategoriesPage })));
const ProductSettingsPage = lazy(() => import('../../../modules/products/src/admin/SettingsPage').then(m => ({ default: m.ProductSettingsPage })));
const WooCommerceImportPage = lazy(() => import('../../../modules/products/src/admin/WooCommerceImportPage').then(m => ({ default: m.WooCommerceImportPage })));
const BlogListPage = lazy(() => import('../../../modules/blog/src/admin/BlogListPage').then(m => ({ default: m.BlogListPage })));
const BlogEditPage = lazy(() => import('../../../modules/blog/src/admin/BlogEditPage').then(m => ({ default: m.BlogEditPage })));
const CommentsListPage = lazy(() => import('./pages/sites/CommentsListPage').then(m => ({ default: m.CommentsListPage })));
const ProjectsListPage = lazy(() => import('../../../modules/projects/src/admin/ProjectsListPage').then(m => ({ default: m.ProjectsListPage })));
const ProjectEditPage = lazy(() => import('../../../modules/projects/src/admin/ProjectEditPage').then(m => ({ default: m.ProjectEditPage })));
const FormsListPage = lazy(() => import('../../../modules/forms/src/admin/FormsListPage').then(m => ({ default: m.FormsListPage })));
const FormBuilderPage = lazy(() => import('../../../modules/forms/src/admin/FormBuilderPage').then(m => ({ default: m.FormBuilderPage })));
const FormSubmissionsPage = lazy(() => import('../../../modules/forms/src/admin/FormSubmissionsPage').then(m => ({ default: m.FormSubmissionsPage })));
const GalleryPage = lazy(() => import('../../../modules/content/src/admin/GalleryPage').then(m => ({ default: m.GalleryPage })));
const TestimonialsPage = lazy(() => import('../../../modules/content/src/admin/TestimonialsPage').then(m => ({ default: m.TestimonialsPage })));
const TeamPage = lazy(() => import('../../../modules/content/src/admin/TeamPage').then(m => ({ default: m.TeamPage })));
const FaqPage = lazy(() => import('../../../modules/content/src/admin/FaqPage').then(m => ({ default: m.FaqPage })));
const AuditLogPage = lazy(() => import('./pages/settings/AuditLogPage').then(m => ({ default: m.AuditLogPage })));
const ProfileSettingsPage = lazy(() => import('./pages/settings/ProfileSettingsPage').then(m => ({ default: m.ProfileSettingsPage })));
const StorageProvidersPage = lazy(() => import('../../../modules/storage/src/admin/StorageProvidersPage').then(m => ({ default: m.StorageProvidersPage })));
const StorageQuotasPage = lazy(() => import('../../../modules/storage/src/admin/StorageQuotasPage').then(m => ({ default: m.StorageQuotasPage })));
const StorageUsageDashboard = lazy(() => import('../../../modules/storage/src/admin/StorageUsageDashboard').then(m => ({ default: m.StorageUsageDashboard })));
const StorageMigrationPage = lazy(() => import('../../../modules/storage/src/admin/StorageMigrationPage').then(m => ({ default: m.StorageMigrationPage })));
const StorageAlertsPage = lazy(() => import('../../../modules/storage/src/admin/StorageAlertsPage').then(m => ({ default: m.StorageAlertsPage })));
const StorageOptimizationPage = lazy(() => import('../../../modules/storage/src/admin/StorageOptimizationPage').then(m => ({ default: m.StorageOptimizationPage })));
const ModuleLimitsPage = lazy(() => import('./pages/admin/ModuleLimitsPage').then(m => ({ default: m.ModuleLimitsPage })));
const UsersManagementPage = lazy(() => import('./pages/admin/UsersManagementPage').then(m => ({ default: m.UsersManagementPage })));
const SystemSettingsPage = lazy(() => import('./pages/admin/SystemSettingsPage').then(m => ({ default: m.SystemSettingsPage })));
const ModuleLimitsSelectPage = lazy(() => import('./pages/admin/ModuleLimitsSelectPage').then(m => ({ default: m.ModuleLimitsSelectPage })));
const PlansManagementPage = lazy(() => import('./pages/admin/PlansManagementPage').then(m => ({ default: m.PlansManagementPage })));
const PlatformStoragePage = lazy(() => import('./pages/admin/PlatformStoragePage').then(m => ({ default: m.PlatformStoragePage })));
const AnalyticsDashboardPage = lazy(() => import('../../../modules/analytics/src/admin/AnalyticsDashboardPage').then(m => ({ default: m.AnalyticsDashboardPage })));
const SeoSettingsPage = lazy(() => import('../../../modules/seo/src/admin/SeoSettingsPage').then(m => ({ default: m.SeoSettingsPage })));
const RolesAndPermissionsPage = lazy(() => import('./pages/settings/RolesAndPermissionsPage'));
const SiteMembersPage = lazy(() => import('./pages/settings/SiteMembersPage'));
const ProfileFieldsPage = lazy(() => import('./pages/sites/ProfileFieldsPage').then(m => ({ default: m.ProfileFieldsPage })));
const SiteUsersPage = lazy(() => import('./pages/sites/SiteUsersPage').then(m => ({ default: m.SiteUsersPage })));
const OrdersPage = lazy(() => import('./pages/sites/OrdersPage').then(m => ({ default: m.OrdersPage })));
const PaymentGatewaysPage = lazy(() => import('./pages/sites/PaymentGatewaysPage').then(m => ({ default: m.PaymentGatewaysPage })));
const StoreSettingsPage = lazy(() => import('./pages/sites/StoreSettingsPage').then(m => ({ default: m.StoreSettingsPage })));

function ProtectedLayout() {
  return (
    <ProtectedRoute>
      <Suspense fallback={<PageLoader />}>
        <AppLayout />
      </Suspense>
    </ProtectedRoute>
  );
}

export const router: ReturnType<typeof createBrowserRouter> = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
    errorElement: <ErrorPage />,
  },
  {
    path: '/reset-password',
    element: <ResetPasswordPage />,
    errorElement: <ErrorPage />,
  },
  {
    path: '/invite/accept',
    element: <AcceptInvitePage />,
    errorElement: <ErrorPage />,
  },
  {
    path: '/',
    element: <ProtectedLayout />,
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: <DashboardPage />,
      },
      {
        path: 'sites',
        children: [
          { index: true, element: <SuperAdminGuard><SitesListPage /></SuperAdminGuard> },
          { path: 'new', element: <SuperAdminGuard><SiteEditPage /></SuperAdminGuard> },
          { path: ':id/edit', element: <SuperAdminGuard><SiteEditPage /></SuperAdminGuard> },
          { path: ':id/users', element: <SuperAdminGuard><SiteUsersPage /></SuperAdminGuard> },
          { path: ':id/orders', element: <SuperAdminGuard><OrdersPage /></SuperAdminGuard> },
          { path: ':id/payment-gateways', element: <SuperAdminGuard><PaymentGatewaysPage /></SuperAdminGuard> },
          { path: ':id/profile-fields', element: <SuperAdminGuard><ProfileFieldsPage /></SuperAdminGuard> },
          { path: ':id/store-settings', element: <SuperAdminGuard><StoreSettingsPage /></SuperAdminGuard> },
          { path: 'preview/:slug', element: <SitePreviewPage /> },
        ],
      },
      {
        path: 'pages',
        children: [
          { index: true, element: <PagesListPage /> },
          { path: 'new', element: <PageEditorPage /> },
          { path: ':id/edit', element: <PageEditorPage /> },
        ],
      },
      {
        path: 'shop',
        children: [
          { index: true, element: <ModuleGuard moduleId="shop"><ProductsListPage /></ModuleGuard> },
          { path: 'products', element: <ModuleGuard moduleId="shop"><Navigate to="/shop" replace /></ModuleGuard> },
          { path: 'products/new', element: <ModuleGuard moduleId="shop"><ProductEditPage /></ModuleGuard> },
          { path: 'products/edit/:id', element: <ModuleGuard moduleId="shop"><ProductEditPage /></ModuleGuard> },
          { path: 'categories', element: <ModuleGuard moduleId="shop"><CategoriesPage /></ModuleGuard> },
          { path: 'settings', element: <ModuleGuard moduleId="shop"><ProductSettingsPage /></ModuleGuard> },
          { path: 'woo-import', element: <ModuleGuard moduleId="shop"><WooCommerceImportPage /></ModuleGuard> },
          { path: 'payment-gateways', element: <ModuleGuard moduleId="shop"><PaymentGatewaysPage /></ModuleGuard> },
          { path: 'orders', element: <ModuleGuard moduleId="shop"><OrdersPage /></ModuleGuard> },
        ],
      },
      {
        path: 'blog',
        children: [
          { index: true, element: <ModuleGuard moduleId="blog"><BlogListPage /></ModuleGuard> },
          { path: 'new', element: <ModuleGuard moduleId="blog"><BlogEditPage /></ModuleGuard> },
          { path: 'edit/:id', element: <ModuleGuard moduleId="blog"><BlogEditPage /></ModuleGuard> },
          { path: 'comments', element: <ModuleGuard moduleId="blog"><CommentsListPage /></ModuleGuard> },
        ],
      },
      {
        path: 'media',
        element: <ModuleGuard moduleId="media"><MediaLibraryPage /></ModuleGuard>,
      },
      {
        path: 'members',
        element: <MembersPage />,
      },
      {
        path: 'forms',
        children: [
          { index: true, element: <ModuleGuard moduleId="forms"><FormsListPage /></ModuleGuard> },
          { path: 'new', element: <ModuleGuard moduleId="forms"><FormBuilderPage /></ModuleGuard> },
          { path: 'edit/:id', element: <ModuleGuard moduleId="forms"><FormBuilderPage /></ModuleGuard> },
          { path: ':formId/submissions', element: <ModuleGuard moduleId="forms"><FormSubmissionsPage /></ModuleGuard> },
        ],
      },
      { path: 'gallery', element: <ModuleGuard moduleId="content"><GalleryPage /></ModuleGuard> },
      { path: 'testimonials', element: <ModuleGuard moduleId="content"><TestimonialsPage /></ModuleGuard> },
      { path: 'team', element: <ModuleGuard moduleId="content"><TeamPage /></ModuleGuard> },
      { path: 'faq', element: <ModuleGuard moduleId="content"><FaqPage /></ModuleGuard> },
      {
        path: 'projects',
        children: [
          { index: true, element: <ModuleGuard moduleId="projects"><ProjectsListPage /></ModuleGuard> },
          { path: 'new', element: <ModuleGuard moduleId="projects"><ProjectEditPage /></ModuleGuard> },
          { path: 'edit/:id', element: <ModuleGuard moduleId="projects"><ProjectEditPage /></ModuleGuard> },
        ],
      },
      {
        path: 'settings',
        element: <SettingsPage />,
      },
      {
        path: 'translations',
        element: <ModuleGuard moduleId="translations"><TranslationsPage /></ModuleGuard>,
      },
      { path: 'theme', element: <SettingsPage /> },
      { path: 'navigation', element: <SettingsPage /> },
      {
        path: 'audit-log',
        element: <AuditLogPage />,
      },
      {
        path: 'profile',
        element: <ProfileSettingsPage />,
      },
      {
        path: 'admin',
        children: [
          { index: true, element: <SuperAdminDashboard /> },
          { path: 'users', element: <UsersManagementPage /> },
          { path: 'system-settings', element: <SystemSettingsPage /> },
          { path: 'plans', element: <PlansManagementPage /> },
          { path: 'module-limits', element: <ModuleLimitsSelectPage /> },
          { path: 'module-limits/:siteId', element: <ModuleLimitsPage /> },
          { path: 'sites', element: <SuperAdminGuard><SitesListPage /></SuperAdminGuard> },
          { path: 'sites/new', element: <SuperAdminGuard><SiteEditPage /></SuperAdminGuard> },
          { path: 'sites/:id/edit', element: <SuperAdminGuard><SiteEditPage /></SuperAdminGuard> },
          { path: 'platform-storage', element: <PlatformStoragePage /> },
        ],
      },
      {
        path: 'storage',
        children: [
          { index: true, element: <StorageProvidersPage /> },
          { path: 'providers', element: <StorageProvidersPage /> },
          { path: 'quotas', element: <StorageQuotasPage /> },
          { path: 'dashboard', element: <StorageUsageDashboard /> },
          { path: 'migration', element: <StorageMigrationPage /> },
          { path: 'alerts', element: <StorageAlertsPage /> },
          { path: 'optimization', element: <StorageOptimizationPage /> },
        ],
      },
      {
        path: 'analytics',
        element: <ModuleGuard moduleId="analytics"><AnalyticsDashboardPage /></ModuleGuard>,
      },
      {
        path: 'seo',
        element: <ModuleGuard moduleId="seo"><SeoSettingsPage /></ModuleGuard>,
      },
      {
        path: 'roles-permissions',
        element: <RolesAndPermissionsPage />,
      },
      {
        path: 'site-members',
        element: <SiteMembersPage />,
      },
    ],
  },
  {
    path: '*',
    element: <ErrorPage />,
  },
]);
